/**
 * 🔄 useJobRequestWorkflow Hook
 * Fecha: 30 de octubre de 2025
 * 
 * Hook personalizado para manejar el flujo de aprobación de dos etapas
 * de las solicitudes de vacantes.
 */

import { useState } from 'react'
import { apiService } from '../services/api'
import type { 
  JobRequest, 
  PreApproveJobRequestDto, 
  AssignSalariesDto, 
  FinalApproveJobRequestDto, 
  ReturnToRHDto 
} from '../services/api'

interface UseJobRequestWorkflowResult {
  loading: boolean
  error: string | null
  preApprove: (id: number, data?: PreApproveJobRequestDto) => Promise<JobRequest | null>
  assignSalaries: (id: number, data: AssignSalariesDto) => Promise<JobRequest | null>
  finalApprove: (id: number, data?: FinalApproveJobRequestDto) => Promise<JobRequest | null>
  returnToRH: (id: number, data: ReturnToRHDto) => Promise<JobRequest | null>
  clearError: () => void
}

export const useJobRequestWorkflow = (): UseJobRequestWorkflowResult => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = () => setError(null)

  /**
   * Pre-aprobar solicitud (Project Manager)
   * Estado requerido: pendiente_validacion_pm → pre_aprobada
   */
  const preApprove = async (
    id: number, 
    data: PreApproveJobRequestDto = {}
  ): Promise<JobRequest | null> => {
    try {
      setLoading(true)
      setError(null)
      
      const updated = await apiService.preApproveJobRequest(id.toString(), data)
      
      return updated
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      const errorMessage = err?.message ?? 'Error al pre-aprobar la solicitud'
      setError(errorMessage)
      console.error('Error en preApprove:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  /**
   * Asignar salarios (Admin RH)
   * Estado requerido: pre_aprobada → en_revision_salarios
   */
  const assignSalaries = async (
    id: number,
    data: AssignSalariesDto
  ): Promise<JobRequest | null> => {
    try {
      setLoading(true)
      setError(null)

      // Validaciones del lado del cliente
      if (data.salarioMinimo <= 0) {
        throw new Error('El salario mínimo debe ser mayor a 0')
      }

      if (data.salarioMaximo <= 0) {
        throw new Error('El salario máximo debe ser mayor a 0')
      }

      if (data.salarioMaximo < data.salarioMinimo) {
        throw new Error('El salario máximo debe ser mayor o igual al salario mínimo')
      }

      const updated = await apiService.assignSalaries(id.toString(), data)
      
      return updated
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al asignar salarios'
      setError(errorMessage)
      console.error('Error en assignSalaries:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  /**
   * Aprobación final (Project Manager)
   * Estado requerido: en_revision_salarios → aprobada
   */
  const finalApprove = async (
    id: number,
    data: FinalApproveJobRequestDto = {}
  ): Promise<JobRequest | null> => {
    try {
      setLoading(true)
      setError(null)

      const updated = await apiService.finalApproveJobRequest(id.toString(), data)
      
      return updated
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al aprobar definitivamente'
      setError(errorMessage)
      console.error('Error en finalApprove:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  /**
   * Devolver a RH (Project Manager)
   * Estado requerido: en_revision_salarios → devuelta_rh
   */
  const returnToRH = async (
    id: number,
    data: ReturnToRHDto
  ): Promise<JobRequest | null> => {
    try {
      setLoading(true)
      setError(null)

      // Validación del lado del cliente
      if (!data.motivo || data.motivo.trim().length === 0) {
        throw new Error('Debe proporcionar un motivo para la devolución')
      }

      if (data.motivo.trim().length < 10) {
        throw new Error('El motivo debe tener al menos 10 caracteres')
      }

      const updated = await apiService.returnToRH(id.toString(), data)
      
      return updated
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al devolver a RH'
      setError(errorMessage)
      console.error('Error en returnToRH:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    error,
    preApprove,
    assignSalaries,
    finalApprove,
    returnToRH,
    clearError
  }
}

